import {
  Box,
  Button,
  Flex,
  Icon,
  Menu,
  MenuButton,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Tag,
  Text,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { ChevronDownIcon, WarningTwoIcon } from "@chakra-ui/icons";
import { MdInfoOutline } from "react-icons/md";
import {
  CatalogoDisparo,
  ContextoDisparo,
  FORMATO_LABEL,
  FormatoVariavel,
  tokenVariavel,
} from "@/models/consignado/disparo.model";
import { VariavelDraft } from "./disparo-draft";
import { propriedade as buscarPropriedade } from "./catalogo.helpers";
import { fieldFont } from "./styleTokens";

type Props = {
  variaveis: VariavelDraft[];
  /** Apelidos que aparecem no texto agora — define o que falta e o que sobra. */
  usadas: string[];
  /** Trechos com chave malformada (`{{1nome}}`, `{{nome`) — ver variaveisMalformadas. */
  malformadas?: string[];
  /** Insere `{{apelido}}` no campo de texto ativo. */
  onInserir: (apelido: string) => void;
  /** Para traduzir a propriedade no rótulo de negócio do campo. */
  catalogo?: CatalogoDisparo;
  contexto: ContextoDisparo;
  /**
   * Troca o formato de uma variável. Sem ele, a lista fica só de leitura.
   * Só aparecem os formatos que o catálogo permite para o campo.
   */
  onFormatoChange?: (variavelId: string, formato: FormatoVariavel) => void;
};

/**
 * Avisos sobre as variáveis da mensagem.
 *
 * Já foi uma tabela **editável**, com origem, formato, obrigatoriedade e valor
 * padrão por variável. A edição da origem saiu e não volta: a variável é
 * definida ao escolher o campo na barra lateral, e deixar remapear aqui dava a
 * impressão de que havia dois caminhos para a mesma coisa.
 *
 * O **formato** voltou a ser trocável, e só ele: é a máscara aplicada ao valor
 * no envio (R$ 1.234,56, 22/09/2026, ***.456.789-**), não muda de onde o valor
 * vem. A variável nasce com o padrão do tipo do campo (formatoPadrao) e aqui se
 * troca entre os formatos que o catálogo permite para aquele campo.
 *
 * A **leitura** voltou. O argumento de que era redundante com a sidebar só
 * vale enquanto se escreve a mensagem: ao abrir um disparo já salvo, quem
 * edita não montou aquele mapeamento e a sidebar não conta nada sobre ele —
 * via `{{nome}}` no texto e nada dizia de onde `nome` saía. Por isso a lista
 * é somente leitura, no mesmo formato que a tela de visualização usa.
 *
 * Os dois avisos continuam sendo o que a sidebar não mostra: todo `{{apelido}}`
 * no texto tem que ter sido inserido por lá, e toda variável inserida tem que
 * continuar no texto — esta segunda se resolve sozinha, porque
 * `limparVariaveisOrfas` descarta a variável assim que o token sai do texto.
 */
export default function VariaveisAvisos({
  variaveis,
  usadas,
  malformadas = [],
  onInserir,
  catalogo,
  contexto,
  onFormatoChange,
}: Props) {
  const apelidos = variaveis.map((v) => v.apelido);

  /** `{{nome}} ← Nome do tomador · Texto`, com o padrão quando é opcional. */
  const mapeamento = variaveis.length > 0 && (
    <Box mt={2}>
      <Text fontSize="xs" color="gray.500" mb={1} {...fieldFont}>
        De onde vem cada variável
        {onFormatoChange && " — clique no formato para trocar a máscara do valor"}
      </Text>
      <Wrap>
        {variaveis.map((v) => {
          const p =
            v.origem === "CONSTANTE" ? undefined : buscarPropriedade(catalogo, contexto, v.propriedade);
          const origem =
            v.origem === "CONSTANTE" ? `"${v.constante}"` : p?.descricao ?? v.propriedade ?? "—";
          const trocavel = !!onFormatoChange && !!p && p.formatos.length > 1;
          return (
            <WrapItem key={v.id}>
              <Tag size="sm" colorScheme="gray" borderRadius="md" {...fieldFont}>
                {tokenVariavel(v.apelido)} ← {origem} ·&nbsp;
                {trocavel ? (
                  <Menu isLazy placement="bottom-start">
                    <MenuButton
                      as={Button}
                      size="xs"
                      variant="link"
                      colorScheme="blue"
                      fontWeight="600"
                      rightIcon={<ChevronDownIcon />}
                      aria-label={`Formato de ${tokenVariavel(v.apelido)}`}
                      {...fieldFont}
                    >
                      {FORMATO_LABEL[v.formato]}
                    </MenuButton>
                    <MenuList zIndex="popover" py={1}>
                      <MenuOptionGroup
                        type="radio"
                        value={v.formato}
                        onChange={(f) => onFormatoChange(v.id, f as FormatoVariavel)}
                      >
                        {p.formatos.map((f) => (
                          <MenuItemOption key={f} value={f} fontSize="xs" {...fieldFont}>
                            {FORMATO_LABEL[f]}
                            <Text as="span" color="gray.500">
                              {" — "}
                              {catalogo?.formatacoes.find((x) => x.id === f)?.exemplo}
                            </Text>
                          </MenuItemOption>
                        ))}
                      </MenuOptionGroup>
                    </MenuList>
                  </Menu>
                ) : (
                  FORMATO_LABEL[v.formato]
                )}
                {v.obrigatoria ? "" : ` · padrão "${v.fallback ?? ""}"`}
              </Tag>
            </WrapItem>
          );
        })}
      </Wrap>
    </Box>
  );
  const semMapeamento = usadas.filter((u) => !apelidos.includes(u));
  const semUso = variaveis.filter((v) => v.apelido && !usadas.includes(v.apelido));

  if (semMapeamento.length === 0 && semUso.length === 0 && malformadas.length === 0) {
    if (variaveis.length === 0) return null;
    return (
      <Box>
        <Flex align="center" gap={1.5} mt={2}>
          <Icon as={MdInfoOutline} boxSize={3.5} color="gray.400" flexShrink={0} />
          <Text fontSize="xs" color="gray.500" {...fieldFont}>
            {variaveis.length} variável(is) na mensagem. Se algum desses campos vier vazio no envio,
            a mensagem não sai para aquele destinatário.
          </Text>
        </Flex>
        {mapeamento}
      </Box>
    );
  }

  return (
    <Box mt={2}>
      {mapeamento}
      {/* O backend recusa estes ao salvar ("Sintaxe de variável inválida");
          aqui aparecem enquanto se escreve, com o trecho que está errado. */}
      {malformadas.length > 0 && (
        <Flex align="center" gap={2} bg="red.50" borderRadius="md" px={3} py={2} mb={2} wrap="wrap">
          <WarningTwoIcon boxSize={3} color="red.500" />
          <Text fontSize="xs" color="red.600" {...fieldFont}>
            Variável mal escrita. Use {"{{nome}}"} com letras sem acento, números ou _, começando
            por letra:
          </Text>
          {malformadas.map((trecho) => (
            <Text key={trecho} fontSize="xs" fontFamily="monospace" color="red.700">
              {trecho}
            </Text>
          ))}
        </Flex>
      )}
      {semMapeamento.length > 0 && (
        <Flex align="center" gap={2} bg="red.50" borderRadius="md" px={3} py={2} mb={2} wrap="wrap">
          <WarningTwoIcon boxSize={3} color="red.500" />
          <Text fontSize="xs" color="red.600" {...fieldFont}>
            A mensagem usa variável que não foi inserida pela barra lateral. Apague do texto e
            escolha o campo por lá:
          </Text>
          {semMapeamento.map((apelido) => (
            <Text key={apelido} fontSize="xs" fontFamily="monospace" color="red.700">
              {tokenVariavel(apelido)}
            </Text>
          ))}
        </Flex>
      )}

      {semUso.length > 0 && (
        <Flex align="center" gap={2} bg="orange.50" borderRadius="md" px={3} py={2} wrap="wrap">
          <WarningTwoIcon boxSize={3} color="orange.500" />
          <Text fontSize="xs" color="orange.700" {...fieldFont}>
            Variável fora do texto:
          </Text>
          {semUso.map((v) => (
            <Button
              key={v.id}
              size="xs"
              variant="outline"
              colorScheme="orange"
              onClick={() => onInserir(v.apelido)}
              {...fieldFont}
            >
              inserir {tokenVariavel(v.apelido)}
            </Button>
          ))}
        </Flex>
      )}
    </Box>
  );
}
